import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { TrendingUp, Mail, MapPin, MessageSquare, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { toast } from "sonner";

const ContactPage = () => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        subject: '',
        message: ''
    });
    const [sending, setSending] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSending(true);

        // Simulate network delay
        await new Promise(resolve => setTimeout(resolve, 1500));

        toast.success("Message sent! We'll get back to you shortly.");
        setFormData({ name: '', email: '', subject: '', message: '' });
        setSending(false);
    };

    return (
        <div className="min-h-screen bg-[#F9F9F7]">
            {/* Header */}
            <header className="fixed top-0 left-0 right-0 z-50 glass border-b border-stone-200/50">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <div className="flex items-center justify-between h-16">
                        <Link to="/" className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-[#1A4D2E] rounded-lg flex items-center justify-center">
                                <TrendingUp className="w-5 h-5 text-white" />
                            </div>
                            <span className="font-serif text-xl font-semibold text-[#1A4D2E]">FictoInvest</span>
                        </Link>
                        <nav className="hidden md:flex items-center gap-8">
                            <Link to="/features" className="text-sm font-medium text-gray-600 hover:text-[#1A4D2E] transition-colors">Features</Link>
                            <Link to="/pricing" className="text-sm font-medium text-gray-600 hover:text-[#1A4D2E] transition-colors">Pricing</Link>
                            <Link to="/about" className="text-sm font-medium text-gray-600 hover:text-[#1A4D2E] transition-colors">About</Link>
                            <Link to="/contact" className="text-sm font-medium text-[#1A4D2E] transition-colors">Contact</Link>
                        </nav>
                        <div className="flex items-center gap-3">
                            <Button
                                variant="ghost"
                                onClick={() => navigate('/login')}
                                className="text-[#1A4D2E] hover:bg-green-50"
                            >
                                Log In
                            </Button>
                            <Button
                                onClick={() => navigate('/register')}
                                className="bg-[#1A4D2E] hover:bg-[#143d24] text-white shadow-lg shadow-green-900/20"
                            >
                                Sign Up
                            </Button>
                        </div>
                    </div>
                </div>
            </header>

            {/* Hero Section - Standardized White */}
            <section className="pt-32 pb-20 px-6 md:px-12 bg-white">
                <div className="max-w-4xl mx-auto text-center space-y-6">
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-50 rounded-full border border-green-100 mb-4">
                        <span className="text-sm font-medium text-[#1A4D2E]">Support</span>
                    </div>
                    <h1 className="font-serif text-4xl md:text-6xl text-[#1F2937] leading-tight">
                        Get in <span className="text-[#1A4D2E]">touch</span>
                    </h1>
                    <p className="text-xl text-gray-600 leading-relaxed max-w-2xl mx-auto">
                        Have questions about the platform? We're here to help you on your investing journey.
                    </p>
                </div>
            </section>

            {/* Contact Content */}
            <section className="py-20 px-6 md:px-12 -mt-16">
                <div className="max-w-6xl mx-auto">
                    <div className="grid lg:grid-cols-3 gap-8">
                        {/* Contact Info */}
                        <div className="lg:col-span-1 space-y-6">
                            <Card className="bg-white border-none shadow-xl">
                                <CardContent className="p-8 space-y-8">
                                    <div className="flex items-start gap-4">
                                        <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center shrink-0">
                                            <Mail className="w-5 h-5 text-[#1A4D2E]" />
                                        </div>
                                        <div>
                                            <h3 className="font-bold text-[#1F2937]">Email Us</h3>
                                            <p className="text-gray-600 text-sm mt-1">support@fictoinvest.com</p>
                                            <p className="text-gray-600 text-sm">press@fictoinvest.com</p>
                                        </div>
                                    </div>

                                    <div className="flex items-start gap-4">
                                        <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center shrink-0">
                                            <MessageSquare className="w-5 h-5 text-[#1A4D2E]" />
                                        </div>
                                        <div>
                                            <h3 className="font-bold text-[#1F2937]">Live Chat</h3>
                                            <p className="text-gray-600 text-sm mt-1">Available Mon-Fri</p>
                                            <p className="text-gray-600 text-sm">9am - 5pm EST</p>
                                        </div>
                                    </div>

                                    <div className="flex items-start gap-4">
                                        <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center shrink-0">
                                            <MapPin className="w-5 h-5 text-[#1A4D2E]" />
                                        </div>
                                        <div>
                                            <h3 className="font-bold text-[#1F2937]">Office</h3>
                                            <p className="text-gray-600 text-sm mt-1">
                                                123 Wall Street<br />
                                                New York, NY 10005
                                            </p>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        </div>

                        {/* Contact Form */}
                        <div className="lg:col-span-2">
                            <Card className="bg-white border-none shadow-xl">
                                <CardContent className="p-8">
                                    <form onSubmit={handleSubmit} className="space-y-6">
                                        <div className="grid md:grid-cols-2 gap-6">
                                            <div className="space-y-2">
                                                <label className="text-sm font-medium text-gray-700">Name</label>
                                                <input
                                                    type="text"
                                                    required
                                                    className="w-full h-10 px-3 rounded-md border border-stone-200 focus:border-[#1A4D2E] focus:ring-1 focus:ring-[#1A4D2E] outline-none transition-all"
                                                    placeholder="John Doe"
                                                    value={formData.name}
                                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <label className="text-sm font-medium text-gray-700">Email</label>
                                                <input
                                                    type="email"
                                                    required
                                                    className="w-full h-10 px-3 rounded-md border border-stone-200 focus:border-[#1A4D2E] focus:ring-1 focus:ring-[#1A4D2E] outline-none transition-all"
                                                    placeholder="john@example.com"
                                                    value={formData.email}
                                                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                                />
                                            </div>
                                        </div>

                                        <div className="space-y-2">
                                            <label className="text-sm font-medium text-gray-700">Subject</label>
                                            <select
                                                className="w-full h-10 px-3 rounded-md border border-stone-200 focus:border-[#1A4D2E] focus:ring-1 focus:ring-[#1A4D2E] outline-none transition-all bg-white"
                                                value={formData.subject}
                                                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                                            >
                                                <option value="">Select a topic...</option>
                                                <option value="support">Technical Support</option>
                                                <option value="feedback">Feedback</option>
                                                <option value="business">Business Inquiry</option>
                                                <option value="other">Other</option>
                                            </select>
                                        </div>

                                        <div className="space-y-2">
                                            <label className="text-sm font-medium text-gray-700">Message</label>
                                            <textarea
                                                required
                                                className="w-full min-h-[150px] p-3 rounded-md border border-stone-200 focus:border-[#1A4D2E] focus:ring-1 focus:ring-[#1A4D2E] outline-none transition-all resize-y"
                                                placeholder="How can we help you?"
                                                value={formData.message}
                                                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                                            />
                                        </div>

                                        <Button
                                            type="submit"
                                            disabled={sending}
                                            className="w-full bg-[#1A4D2E] hover:bg-[#143d24] text-white py-6 text-lg"
                                        >
                                            {sending ? "Sending..." : <span className="flex items-center">Send Message <Send className="ml-2 w-4 h-4" /></span>}
                                        </Button>
                                    </form>
                                </CardContent>
                            </Card>
                        </div>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="py-12 px-6 md:px-12 border-t border-stone-200 bg-white">
                <div className="max-w-7xl mx-auto text-center">
                    <div className="flex flex-col md:flex-row justify-between items-center gap-6">
                        <div className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-[#1A4D2E] rounded-lg flex items-center justify-center">
                                <TrendingUp className="w-5 h-5 text-white" />
                            </div>
                            <span className="font-serif text-xl font-semibold text-[#1A4D2E]">FictoInvest</span>
                        </div>
                        <div className="flex gap-8">
                            <Link to="/features" className="text-gray-600 hover:text-[#1A4D2E]">Features</Link>
                            <Link to="/pricing" className="text-gray-600 hover:text-[#1A4D2E]">Pricing</Link>
                            <Link to="/about" className="text-gray-600 hover:text-[#1A4D2E]">About</Link>
                            <Link to="/contact" className="text-gray-600 hover:text-[#1A4D2E]">Contact</Link>
                            <Link to="/privacy" className="text-gray-600 hover:text-[#1A4D2E]">Privacy</Link>
                        </div>
                        <p className="text-sm text-gray-500">
                            © {new Date().getFullYear()} FictoInvest. All rights reserved.
                        </p>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default ContactPage;
